import type { DiagramTool } from '../modes/diagram/state/types';
import type { ArrowHead, TextStyle } from '../modes/diagram/state/types';
import { DEFAULT_TEXT_STYLE, SHAPE_PRESETS, SHAPE_PRESETS_DARK } from '../modes/diagram/state/types';
import type { ToolType } from '../types';
import { TOOLBAR_ICONS } from './icons';
import { setToggleButtonState } from './toolbarParts';
import { ToolbarBase } from './ToolbarBase';
import { DRAW_SHORTCUTS } from '../input/shortcuts';
import { ANIMATION, FONT_FAMILIES } from '../config/constants';

export type LineStyle = 'solid' | 'dashed' | 'dotted';

type ShapeSubtype = 'draw' | 'diagram';

// Merged tool type covering both modes
type UnifiedToolbarTool = ToolType | DiagramTool | 'connector' | 'draw' | 'cylinder';

export interface UnifiedToolbarCallbacks {
  // Tool / mode
  onToolChange: (tool: UnifiedToolbarTool) => void;
  onShapeSubtypeChange: (subtype: ShapeSubtype) => void;

  // Draw-mode callbacks
  onColorChange: (color: string) => void;
  onSizeChange: (size: number) => void;
  onStrokeEraseModeToggle?: (enabled: boolean) => void;

  // Diagram-mode callbacks
  onStyleChange: (style: { fill?: string; stroke?: string; strokeWidth?: number }) => void;
  onConnectorStyleChange?: (style: { startArrow?: ArrowHead; endArrow?: ArrowHead; lineStyle?: LineStyle; strokeWidth?: number }) => void;
  onToggleArc?: () => void;
  onDeleteSelected?: () => void;
  onFitToScreen?: () => void;

  // Shared
  onTextStyleChange?: (style: Partial<TextStyle>) => void;
  onLineFormatChange?: (type: 'normal') => void;
  onTextListAction?: (kind: 'ordered' | 'unordered') => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onClear: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetView?: () => void;
  onPanModeChange?: (enabled: boolean) => void;
  onGridToggle?: (enabled: boolean) => void;
  onSnapToggle?: (enabled: boolean) => void;
  onInvertToggle?: (enabled: boolean) => void;
  onShowShortcuts?: () => void;
  onExport?: () => void;
}

const ICONS = TOOLBAR_ICONS;

// Preset sizes for quick selection
const PRESET_SIZES = {
  thin: 2,
  regular: 8,
  thick: 24,
} as const;

const MIN_SIZE = 1;
const MAX_SIZE = 100;
const DEFAULT_PEN_SIZE = 8;
const DEFAULT_ERASER_SIZE = 40;

const TEXT_FONT_SIZE_PRESETS = [
  { label: 'S', value: 16 },
  { label: 'M', value: 24 },
  { label: 'L', value: 36 },
] as const;
const TEXT_FONT_OPTIONS = [
  { label: 'Inter', value: FONT_FAMILIES.ui },
  { label: 'DM Sans', value: FONT_FAMILIES.connector },
  { label: 'Georgia', value: '"Georgia", serif' },
  { label: 'Menlo', value: '"Menlo", monospace' },
] as const;

export class UnifiedToolbar extends ToolbarBase {
  private readonly container: HTMLElement;
  private readonly callbacks: UnifiedToolbarCallbacks;
  private currentTool: UnifiedToolbarTool = 'pen';
  private shapeSubtype: ShapeSubtype = 'draw';

  // Draw mode state
  private currentColor = '#000000';
  private currentSize: number = DEFAULT_PEN_SIZE;
  private penSize: number = DEFAULT_PEN_SIZE;
  private eraserSize: number = DEFAULT_ERASER_SIZE;
  private shapeSize: number = DEFAULT_PEN_SIZE;
  private colorSwatches: string[] = ['#000000', '#ffffff', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b'];
  private strokeEraseMode = true;
  private strokeEraseModeBtn: HTMLButtonElement | null = null;

  // Sidebar
  private sidebarEl: HTMLElement | null = null;
  private sizeSlider: HTMLInputElement | null = null;
  private sizeDisplay: HTMLSpanElement | null = null;
  private sliderAnimationId: number | null = null;

  // Shape subtype toggle (in top bar)
  private drawTabBtn: HTMLButtonElement | null = null;
  private diagramTabBtn: HTMLButtonElement | null = null;
  private cylinderBtn: HTMLButtonElement | null = null;
  private connectorBtn: HTMLButtonElement | null = null;

  // Shape tools row (inline in top bar)
  private shapeToolsRow: HTMLElement | null = null;
  private lastShapeTool: ToolType = 'rectangle';

  // Diagram top bar
  private presetPicker: HTMLDivElement | null = null;
  private fillColorInput: HTMLInputElement | null = null;
  private strokeColorInput: HTMLInputElement | null = null;
  private themeObserver: MutationObserver | null = null;

  // Delete + history
  private deleteSelectedBtn: HTMLButtonElement | null = null;

  // Text formatting
  private textFormatRow: HTMLElement | null = null;
  private fontFamilySelect: HTMLSelectElement | null = null;
  private fontSizeSelect: HTMLSelectElement | null = null;
  private boldBtn: HTMLButtonElement | null = null;
  private italicBtn: HTMLButtonElement | null = null;
  private fontColorInput: HTMLInputElement | null = null;
  private alignLeftBtn: HTMLButtonElement | null = null;
  private alignCenterBtn: HTMLButtonElement | null = null;
  private alignRightBtn: HTMLButtonElement | null = null;
  private listOrderedBtn: HTMLButtonElement | null = null;
  private listUnorderedBtn: HTMLButtonElement | null = null;
  private currentTextStyle: TextStyle = { ...DEFAULT_TEXT_STYLE };

  // Connector options
  private connectorOptionsSection: HTMLElement | null = null;
  private startArrowBtn: HTMLButtonElement | null = null;
  private endArrowBtn: HTMLButtonElement | null = null;
  private hasStartArrow = false;
  private hasEndArrow = true;
  private solidBtn: HTMLButtonElement | null = null;
  private dashedBtn: HTMLButtonElement | null = null;
  private dottedBtn: HTMLButtonElement | null = null;
  private arcBtn: HTMLButtonElement | null = null;
  private currentLineStyle: LineStyle = 'solid';
  private hasArc = false;

  // Stroke width (diagram shapes)
  private strokeWidthSlider: HTMLInputElement | null = null;
  private strokeWidthDisplay: HTMLSpanElement | null = null;
  private currentStrokeWidth = 2;

  constructor(container: HTMLElement, callbacks: UnifiedToolbarCallbacks) {
    super();
    this.container = container;
    this.callbacks = callbacks;
    this.gridEnabled = true;
    this.snapEnabled = true;
    this.actionButtonOptions = { preventPenHover: true };
    this.currentTextStyle = { ...DEFAULT_TEXT_STYLE, color: this.currentColor };
    this.renderToolbar();
    this.setupThemeObserver();
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          this.updatePresetButtons();
          this.updateColorPickerDefaults();
        }
      }
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  // --- Render ---

  private renderToolbar(): void {
    // Clear container safely
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    this.container.className = 'toolbar diagram-toolbar-minimal';

    // -- LEFT SIDEBAR --
    this.sidebarEl = this.createSidebarContainer('draw-sidebar', 'draw-sidebar');

    const selectTool = (tool: UnifiedToolbarTool): void => {
      const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
      if (tool === 'rectangle' && !shapeTools.includes(this.currentTool as ToolType) && this.currentTool !== 'cylinder' && this.currentTool !== 'connector') {
        this.switchToTool(this.lastShapeTool);
      } else if (this.currentTool === tool && tool !== 'pen') {
        this.switchToTool('pen');
      } else {
        this.switchToTool(tool);
      }
    };

    const toolButtons: Array<{ tool: UnifiedToolbarTool; icon: string; title: string }> = [
      { tool: 'select', icon: ICONS.select, title: 'Select (V)' },
      { tool: 'pen', icon: ICONS.pen, title: 'Pen (P)' },
      { tool: 'eraser', icon: ICONS.eraser, title: 'Eraser (E)' },
      { tool: 'text', icon: ICONS.text, title: 'Text (T)' },
      { tool: 'rectangle', icon: ICONS.shapes, title: 'Shapes (R)' },
    ];
    const handlePanToggle = (): void => {
      this.togglePanMode(this.sidebarEl, this.currentTool as string, this.callbacks.onPanModeChange);
    };
    const toolsSection = this.createSidebarToolSection({
      tools: toolButtons.map(({ tool, icon, title }) => ({
        tool: tool as string,
        icon,
        title,
        onClick: (): void => selectTool(tool),
      })),
      pan: {
        icon: ICONS.pan,
        title: 'Pan (Hold Space)',
        onClick: handlePanToggle,
      },
    });
    this.sidebarEl.appendChild(toolsSection);
    this.sidebarEl.appendChild(this.createDivider('sidebar-divider'));

    // -- Brush / stroke size section --
    const sizesSection = this.createSidebarSection('sizes-section');
    const sizesContainer = document.createElement('div');
    sizesContainer.className = 'sizes-container';

    const presetsColumn = document.createElement('div');
    presetsColumn.className = 'presets-column';
    const presetKeys = ['thin', 'regular', 'thick'] as const;
    for (const key of presetKeys) {
      presetsColumn.appendChild(this.createBrushSizeButton(key, PRESET_SIZES[key]));
    }
    sizesContainer.appendChild(presetsColumn);

    const sliderWrapper = document.createElement('div');
    sliderWrapper.className = 'size-slider-wrapper';
    this.sizeSlider = document.createElement('input');
    this.sizeSlider.type = 'range';
    this.sizeSlider.className = 'size-slider';
    this.sizeSlider.min = String(MIN_SIZE);
    this.sizeSlider.max = String(MAX_SIZE);
    this.sizeSlider.value = String(this.currentSize);
    this.sizeSlider.title = `Size: ${this.currentSize}`;
    this.sizeSlider.addEventListener('input', (): void => {
      const newSize = parseInt(this.sizeSlider!.value, 10);
      this.setCurrentSize(newSize);
    });
    sliderWrapper.appendChild(this.sizeSlider);

    this.sizeDisplay = document.createElement('span');
    this.sizeDisplay.className = 'size-display';
    this.sizeDisplay.textContent = `${this.currentSize}`;
    sliderWrapper.appendChild(this.sizeDisplay);
    sizesContainer.appendChild(sliderWrapper);
    sizesSection.appendChild(sizesContainer);
    this.sidebarEl.appendChild(sizesSection);
    this.sidebarEl.appendChild(this.createDivider('sidebar-divider'));

    // -- Color swatches --
    const colorsSection = this.createSidebarSection('colors-section');
    for (let i = 0; i < this.colorSwatches.length; i++) {
      colorsSection.appendChild(this.createColorSwatch(i));
    }
    const customColorWrapper = document.createElement('div');
    customColorWrapper.className = 'custom-color-wrapper';
    const customColorInput = document.createElement('input');
    customColorInput.type = 'color';
    customColorInput.className = 'custom-color-picker';
    customColorInput.value = this.currentColor;
    customColorInput.title = 'Custom Color';
    customColorInput.addEventListener('input', (): void => {
      this.currentColor = customColorInput.value;
      this.callbacks.onColorChange(this.currentColor);
      this.updateColorSwatches();
    });
    customColorWrapper.appendChild(customColorInput);
    colorsSection.appendChild(customColorWrapper);
    this.sidebarEl.appendChild(colorsSection);
    this.mountBeforeCanvas(this.sidebarEl);

    // -- RIGHT PANEL: Connector options --
    this.connectorOptionsSection = document.createElement('div');
    this.connectorOptionsSection.className = 'connector-options-panel';
    this.renderConnectorPanel();
    this.mountAfterCanvas(this.connectorOptionsSection);

    // -- TOP BAR --
    const topBar = this.createToolbarRow('toolbar-row style-row');

    // Style group
    const colorGroup = this.createGroup('Style');
    colorGroup.classList.add('style-group');
    colorGroup.appendChild(this.createPresetPicker());
    colorGroup.appendChild(this.createColorPicker('fill', 'Fill'));
    colorGroup.appendChild(this.createColorPicker('stroke', 'Stroke'));

    // Actions
    const handleUndo = (): void => this.callbacks.onUndo?.();
    const handleRedo = (): void => this.callbacks.onRedo?.();
    const handleDeleteSelected = (): void => this.callbacks.onDeleteSelected?.();
    const handleClear = (): void => this.callbacks.onClear();
    const handleStrokeEraseToggle = (): void => {
      this.strokeEraseMode = !this.strokeEraseMode;
      setToggleButtonState(this.strokeEraseModeBtn, this.strokeEraseMode);
      this.callbacks.onStrokeEraseModeToggle?.(this.strokeEraseMode);
    };
    const actionGroupControls = this.buildActionGroup({
      actions: [
        { key: 'deleteSelected', icon: ICONS.delete, title: 'Delete Selected (Del)', onClick: handleDeleteSelected, className: 'delete-btn', disabled: true },
        { key: 'clear', kind: 'clear', icon: ICONS.clear, title: 'Clear All', onClick: handleClear },
        { key: 'strokeErase', icon: ICONS.strokeErase, title: 'Stroke Erase Mode - erase whole strokes', onClick: handleStrokeEraseToggle, testId: 'stroke-erase-toggle' },
      ],
    });
    const actionGroup = actionGroupControls.group;
    this.deleteSelectedBtn = actionGroupControls.buttons['deleteSelected'] ?? null;
    this.strokeEraseModeBtn = actionGroupControls.buttons['strokeErase'] ?? null;
    setToggleButtonState(this.strokeEraseModeBtn, this.strokeEraseMode);

    // Top bar shared groups
    const handleExport = (): void => this.callbacks.onExport?.();
    const handleZoomOut = (): void => this.callbacks.onZoomOut?.();
    const handleZoomIn = (): void => this.callbacks.onZoomIn?.();
    const handleResetView = (): void => this.callbacks.onResetView?.();
    const handleFitToScreen = (): void => this.callbacks.onFitToScreen?.();
    const handleGridToggle = (enabled: boolean): void => this.callbacks.onGridToggle?.(enabled);
    const handleSnapToggle = (enabled: boolean): void => this.callbacks.onSnapToggle?.(enabled);
    const handleInvertToggle = (enabled: boolean): void => this.callbacks.onInvertToggle?.(enabled);
    const handleShowShortcuts = (): void => this.callbacks.onShowShortcuts?.();

    const topBarGroups = this.buildTopBarGroups({
      history: {
        undo: { icon: ICONS.undo, title: 'Undo (Cmd Z)', onClick: handleUndo, disabled: true },
        redo: { icon: ICONS.redoDiagram, title: 'Redo (Cmd Shift Z)', onClick: handleRedo, disabled: true },
      },
      export: { icon: ICONS.export, title: 'Export', onClick: handleExport },
      view: {
        zoomOut: { icon: ICONS.zoomOut, title: 'Zoom Out (Cmd -)', onClick: handleZoomOut },
        zoomIn: { icon: ICONS.zoomIn, title: 'Zoom In (Cmd +)', onClick: handleZoomIn },
        reset: { onClick: handleResetView },
        fitToScreen: { icon: ICONS.fitToScreen, title: 'Fit to Screen', onClick: handleFitToScreen },
        grid: { icon: ICONS.grid, title: 'Toggle Grid (G)', onToggle: handleGridToggle },
        snap: { icon: ICONS.snap, title: 'Snap to Grid (Shift+S)', onToggle: handleSnapToggle },
        invert: { icon: ICONS.invert, title: 'Invert Canvas (I)', onToggle: handleInvertToggle },
        help: { icon: ICONS.help, title: 'Keyboard Shortcuts (?)', onClick: handleShowShortcuts },
      },
    });

    topBar.appendChild(colorGroup);
    topBar.appendChild(topBarGroups.historyGroup);
    topBar.appendChild(actionGroup);

    // Shape tools inline row (Draw/Diagram toggle + shape buttons + connector)
    this.shapeToolsRow = document.createElement('div');
    this.shapeToolsRow.className = 'shape-tools-inline';
    this.shapeToolsRow.style.display = 'none';

    // Draw / Diagram toggle tabs
    const tabRow = document.createElement('div');
    tabRow.className = 'shape-subtype-tabs';
    this.drawTabBtn = document.createElement('button');
    this.drawTabBtn.className = 'shape-subtype-tab active';
    this.drawTabBtn.textContent = 'Draw';
    this.drawTabBtn.title = 'Draw shapes (drag to create)';
    this.drawTabBtn.addEventListener('click', (): void => this.setShapeSubtype('draw'));
    this.diagramTabBtn = document.createElement('button');
    this.diagramTabBtn.className = 'shape-subtype-tab';
    this.diagramTabBtn.textContent = 'Diagram';
    this.diagramTabBtn.title = 'Diagram shapes (click to place)';
    this.diagramTabBtn.addEventListener('click', (): void => this.setShapeSubtype('diagram'));
    tabRow.appendChild(this.drawTabBtn);
    tabRow.appendChild(this.diagramTabBtn);
    this.shapeToolsRow.appendChild(tabRow);

    // Shape buttons
    const shapesInlineGroup = this.createGroup('Shapes');
    const inlineShapeButtons: Array<{ tool: UnifiedToolbarTool; icon: string; title: string }> = [
      { tool: 'rectangle', icon: ICONS.rectangle, title: 'Rectangle (R)' },
      { tool: 'ellipse', icon: ICONS.ellipse, title: 'Ellipse (O)' },
      { tool: 'diamond', icon: ICONS.diamond, title: 'Diamond (D)' },
      { tool: 'triangle', icon: ICONS.triangle, title: 'Triangle (G)' },
    ];
    for (const { tool, icon, title } of inlineShapeButtons) {
      const btn = this.createActionButton(icon, title, (): void => selectTool(tool));
      btn.dataset['tool'] = tool;
      shapesInlineGroup.appendChild(btn);
    }
    // Cylinder (diagram only)
    const cylinderBtn = this.createActionButton(ICONS.cylinder, 'Cylinder (C)', (): void => selectTool('cylinder'));
    cylinderBtn.dataset['tool'] = 'cylinder';
    shapesInlineGroup.appendChild(cylinderBtn);
    this.cylinderBtn = cylinderBtn;
    // Connector (diagram only)
    const connectorBtn = this.createActionButton(ICONS.connector, 'Connector (L)', (): void => selectTool('connector'));
    connectorBtn.dataset['tool'] = 'connector';
    shapesInlineGroup.appendChild(connectorBtn);
    this.connectorBtn = connectorBtn;

    this.shapeToolsRow.appendChild(shapesInlineGroup);
    this.updateDiagramOnlyButtons();
    topBar.appendChild(this.shapeToolsRow);

    topBar.appendChild(topBarGroups.exportGroup);
    this.container.appendChild(topBar);

    // Text format row — floating panel below toolbar (outside overflow container)
    this.textFormatRow = document.createElement('div');
    this.textFormatRow.className = 'toolbar-row text-format-inline';
    this.textFormatRow.style.display = 'none';
    this.renderTextFormatControls(this.textFormatRow);
    // Append to toolbar parent so it floats outside overflow:hidden
    const toolbarParent = this.container.parentElement;
    if (toolbarParent) {
      toolbarParent.appendChild(this.textFormatRow);
    } else {
      this.container.appendChild(this.textFormatRow);
    }

    // Final setup
    this.updateToolButtons();
    this.updateBrushSizeButtons();
    this.updateColorSwatches();
    this.updateTextFormatButtons();
    this.updateShapeToolsVisibility();
    this.setupKeyboardShortcuts();
  }

  // --- Connector panel ---

  private renderConnectorPanel(): void {
    const panel = this.connectorOptionsSection!;
    panel.appendChild(this.createSidebarLabel('Arrows'));

    const handleStartArrowToggle = (): void => {
      this.hasStartArrow = !this.hasStartArrow;
      this.updateArrowButtons();
      this.callbacks.onConnectorStyleChange?.({ startArrow: this.hasStartArrow ? 'arrow' : 'none' });
    };
    const handleEndArrowToggle = (): void => {
      this.hasEndArrow = !this.hasEndArrow;
      this.updateArrowButtons();
      this.callbacks.onConnectorStyleChange?.({ endArrow: this.hasEndArrow ? 'arrow' : 'none' });
    };
    const arrowButtons = this.appendSidebarToolButtons({
      container: panel,
      buttons: [
        { key: 'startArrow', tool: 'arrow-start', icon: ICONS.arrowLeft, title: 'Toggle Start Arrow', onClick: handleStartArrowToggle },
        { key: 'endArrow', tool: 'arrow-end', icon: ICONS.arrowRight, title: 'Toggle End Arrow', onClick: handleEndArrowToggle },
      ],
    });
    this.startArrowBtn = arrowButtons['startArrow'] ?? null;
    this.endArrowBtn = arrowButtons['endArrow'] ?? null;

    panel.appendChild(this.createDivider('sidebar-divider'));
    panel.appendChild(this.createSidebarLabel('Style'));

    const applyLineStyle = (style: LineStyle): void => {
      this.currentLineStyle = style;
      this.updateLineStyleButtons();
      this.callbacks.onConnectorStyleChange?.({ lineStyle: style });
    };
    const lineStyleButtons = this.appendSidebarToolButtons({
      container: panel,
      buttons: [
        { key: 'solid', tool: 'line-solid', icon: ICONS.lineSolid, title: 'Solid Line', onClick: (): void => applyLineStyle('solid'), active: this.currentLineStyle === 'solid' },
        { key: 'dashed', tool: 'line-dashed', icon: ICONS.lineDashed, title: 'Dashed Line', onClick: (): void => applyLineStyle('dashed'), active: this.currentLineStyle === 'dashed' },
        { key: 'dotted', tool: 'line-dotted', icon: ICONS.lineDotted, title: 'Dotted Line', onClick: (): void => applyLineStyle('dotted'), active: this.currentLineStyle === 'dotted' },
      ],
    });
    this.solidBtn = lineStyleButtons['solid'] ?? null;
    this.dashedBtn = lineStyleButtons['dashed'] ?? null;
    this.dottedBtn = lineStyleButtons['dotted'] ?? null;

    panel.appendChild(this.createDivider('sidebar-divider'));
    panel.appendChild(this.createSidebarLabel('Curve'));

    const arcButtons = this.appendSidebarToolButtons({
      container: panel,
      buttons: [
        { key: 'arc', tool: 'line-arc', icon: ICONS.arc, title: 'Toggle Arc (adds draggable curve point)', onClick: (): void => { this.callbacks.onToggleArc?.(); } },
      ],
    });
    this.arcBtn = arcButtons['arc'] ?? null;
  }

  // --- Text format controls ---

  private renderTextFormatControls(container: HTMLElement): void {
    const fontGroup = this.createGroup('Text');

    this.fontFamilySelect = document.createElement('select');
    this.fontFamilySelect.className = 'font-family-select';
    this.fontFamilySelect.title = 'Font';
    for (const option of TEXT_FONT_OPTIONS) {
      const optionEl = document.createElement('option');
      optionEl.value = option.value;
      optionEl.textContent = option.label;
      if (option.value === this.currentTextStyle.fontFamily) optionEl.selected = true;
      this.fontFamilySelect.appendChild(optionEl);
    }
    this.fontFamilySelect.addEventListener('change', (): void => {
      this.applyTextStyle({ fontFamily: this.fontFamilySelect!.value });
    });
    fontGroup.appendChild(this.fontFamilySelect);

    this.fontSizeSelect = document.createElement('select');
    this.fontSizeSelect.className = 'font-size-select';
    this.fontSizeSelect.title = 'Font Size';
    let selectedIndex = 0;
    for (let i = 0; i < TEXT_FONT_SIZE_PRESETS.length; i++) {
      const preset = TEXT_FONT_SIZE_PRESETS[i]!;
      const option = document.createElement('option');
      option.value = String(preset.value);
      option.textContent = preset.label;
      if (preset.value === this.currentTextStyle.fontSize) {
        option.selected = true;
        selectedIndex = i;
      }
      this.fontSizeSelect.appendChild(option);
    }
    this.fontSizeSelect.selectedIndex = selectedIndex;
    this.fontSizeSelect.addEventListener('change', (): void => {
      this.applyTextStyle({ fontSize: parseInt(this.fontSizeSelect!.value, 10) });
    });
    fontGroup.appendChild(this.fontSizeSelect);

    const handleBoldToggle = (): void => {
      this.applyTextStyle({ fontWeight: this.currentTextStyle.fontWeight === 'bold' ? 'normal' : 'bold' });
    };
    const handleItalicToggle = (): void => {
      this.applyTextStyle({ fontStyle: this.currentTextStyle.fontStyle === 'italic' ? 'normal' : 'italic' });
    };
    const formatButtons = this.appendToggleButtons({
      container: fontGroup,
      buttons: [
        { key: 'bold', icon: ICONS.bold, title: 'Bold', onClick: handleBoldToggle },
        { key: 'italic', icon: ICONS.italic, title: 'Italic', onClick: handleItalicToggle },
      ],
    });
    this.boldBtn = formatButtons['bold'] ?? null;
    this.italicBtn = formatButtons['italic'] ?? null;

    const fontColorWrapper = document.createElement('div');
    fontColorWrapper.className = 'color-picker-wrapper';
    fontColorWrapper.title = 'Font Color';
    fontColorWrapper.style.setProperty('--font-color', this.currentTextStyle.color);
    this.fontColorInput = document.createElement('input');
    this.fontColorInput.type = 'color';
    this.fontColorInput.value = this.currentTextStyle.color;
    this.fontColorInput.className = 'color-picker font-color-picker';
    this.fontColorInput.addEventListener('input', (): void => {
      const color = this.fontColorInput!.value;
      fontColorWrapper.style.setProperty('--font-color', color);
      this.applyTextStyle({ color });
    });
    fontColorWrapper.appendChild(this.fontColorInput);
    fontGroup.appendChild(fontColorWrapper);
    container.appendChild(fontGroup);

    const alignGroup = this.createGroup('Align');
    const alignButtons = this.appendToggleButtons({
      container: alignGroup,
      buttons: [
        { key: 'left', icon: ICONS.alignLeft, title: 'Align Left', onClick: (): void => this.applyTextStyle({ textAlign: 'left' }) },
        { key: 'center', icon: ICONS.alignCenter, title: 'Align Center', onClick: (): void => this.applyTextStyle({ textAlign: 'center' }) },
        { key: 'right', icon: ICONS.alignRight, title: 'Align Right', onClick: (): void => this.applyTextStyle({ textAlign: 'right' }) },
      ],
    });
    this.alignLeftBtn = alignButtons['left'] ?? null;
    this.alignCenterBtn = alignButtons['center'] ?? null;
    this.alignRightBtn = alignButtons['right'] ?? null;
    container.appendChild(alignGroup);

    const listGroup = this.createGroup('List');
    this.listUnorderedBtn = this.createActionButton(ICONS.listUnordered, 'Unordered List', (): void => this.callbacks.onTextListAction?.('unordered'));
    this.listOrderedBtn = this.createActionButton(ICONS.listOrdered, 'Ordered List', (): void => this.callbacks.onTextListAction?.('ordered'));
    listGroup.appendChild(this.listUnorderedBtn);
    listGroup.appendChild(this.listOrderedBtn);
    container.appendChild(listGroup);
  }

  // --- Text style helpers ---

  private applyTextStyle(update: Partial<TextStyle>): void {
    this.currentTextStyle = { ...this.currentTextStyle, ...update };
    this.updateTextFormatButtons();
    this.callbacks.onTextStyleChange?.(update);
  }

  private updateTextFormatButtons(): void {
    const toggles: Array<{ btn: HTMLButtonElement | null; enabled: boolean }> = [
      { btn: this.boldBtn, enabled: this.currentTextStyle.fontWeight === 'bold' },
      { btn: this.italicBtn, enabled: this.currentTextStyle.fontStyle === 'italic' },
      { btn: this.alignLeftBtn, enabled: this.currentTextStyle.textAlign === 'left' },
      { btn: this.alignCenterBtn, enabled: this.currentTextStyle.textAlign === 'center' },
      { btn: this.alignRightBtn, enabled: this.currentTextStyle.textAlign === 'right' },
    ];
    for (const toggle of toggles) {
      setToggleButtonState(toggle.btn, toggle.enabled);
    }
    if (this.fontFamilySelect) {
      this.fontFamilySelect.value = this.currentTextStyle.fontFamily;
    }
    if (this.fontSizeSelect) {
      // Select closest S/M/L preset
      let bestIndex = 0;
      let bestDiff = Infinity;
      for (let i = 0; i < TEXT_FONT_SIZE_PRESETS.length; i++) {
        const diff = Math.abs(TEXT_FONT_SIZE_PRESETS[i]!.value - this.currentTextStyle.fontSize);
        if (diff < bestDiff) { bestDiff = diff; bestIndex = i; }
      }
      this.fontSizeSelect.selectedIndex = bestIndex;
    }
    if (this.fontColorInput) {
      this.fontColorInput.value = this.currentTextStyle.color;
      const colorWrapper = this.fontColorInput.parentElement;
      if (colorWrapper) colorWrapper.style.setProperty('--font-color', this.currentTextStyle.color);
    }
  }

  // --- Shape subtype toggle ---

  setShapeSubtype(subtype: ShapeSubtype): void {
    if (subtype === this.shapeSubtype) return;
    this.shapeSubtype = subtype;
    this.drawTabBtn?.classList.toggle('active', subtype === 'draw');
    this.diagramTabBtn?.classList.toggle('active', subtype === 'diagram');
    this.updateDiagramOnlyButtons();
    this.updateShapeToolsVisibility();
    this.callbacks.onShapeSubtypeChange(subtype);
  }

  getShapeSubtype(): ShapeSubtype {
    return this.shapeSubtype;
  }

  private updateDiagramOnlyButtons(): void {
    const show = this.shapeSubtype === 'diagram';
    if (this.cylinderBtn) this.cylinderBtn.style.display = show ? '' : 'none';
    if (this.connectorBtn) this.connectorBtn.style.display = show ? '' : 'none';
  }

  // --- Tool switching ---

  private switchToTool(tool: UnifiedToolbarTool): void {
    const previousTool = this.currentTool;
    this.currentTool = tool;
    this.panMode = false;

    const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
    if (shapeTools.includes(tool as ToolType)) {
      this.lastShapeTool = tool as ToolType;
    }

    if (previousTool === 'pen') this.penSize = this.currentSize;
    else if (previousTool === 'eraser') this.eraserSize = this.currentSize;
    else if (shapeTools.includes(previousTool as ToolType)) this.shapeSize = this.currentSize;

    this.updateToolButtons();
    this.updateSliderMode();
    this.updateShapeToolsVisibility();
    this.callbacks.onToolChange(tool);
    this.callbacks.onPanModeChange?.(false);

    if (tool === 'pen') {
      this.currentSize = this.penSize;
      this.updateBrushSizeButtons();
      this.callbacks.onSizeChange(this.currentSize);
    } else if (tool === 'eraser') {
      this.currentSize = this.eraserSize;
      this.updateBrushSizeButtons();
      this.callbacks.onSizeChange(this.currentSize);
    } else if (shapeTools.includes(tool as ToolType)) {
      this.currentSize = this.shapeSize;
      this.updateBrushSizeButtons();
      this.callbacks.onSizeChange(this.currentSize);
    }
  }

  private updateSliderMode(): void {
    if (this.sizeSlider) {
      const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
      this.sizeSlider.classList.toggle('eraser-mode', this.currentTool === 'eraser');
      this.sizeSlider.classList.toggle('shape-mode', shapeTools.includes(this.currentTool as ToolType) || this.currentTool === 'cylinder');
    }
  }

  // --- Tool button updates ---

  private updateToolButtons(): void {
    const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
    this.updateSidebarToolButtons(this.sidebarEl, this.currentTool as string);

    // Override shapes button in tools section to be active for any shape tool
    const toolsSections = this.sidebarEl?.querySelectorAll('.sidebar-section');
    if (toolsSections && toolsSections.length > 0) {
      const firstSection = toolsSections[0];
      const shapesButton = firstSection?.querySelector('[data-tool="rectangle"]') as HTMLButtonElement | null;
      if (shapesButton) {
        const isShapeTool = shapeTools.includes(this.currentTool as ToolType) || this.currentTool === 'cylinder' || this.currentTool === 'connector';
        shapesButton.classList.toggle('active', isShapeTool && !this.panMode);
      }
    }

    const shapeButtons = this.shapeToolsRow?.querySelectorAll('[data-tool]');
    shapeButtons?.forEach((btn): void => {
      const button = btn as HTMLButtonElement;
      const tool = button.dataset['tool'] as ToolType;
      button.classList.toggle('active', tool === this.currentTool);
    });
  }

  private updateShapeToolsVisibility(): void {
    const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
    const isShapeTool = shapeTools.includes(this.currentTool as ToolType) || this.currentTool === 'cylinder' || this.currentTool === 'connector';
    if (this.shapeToolsRow) {
      this.shapeToolsRow.style.display = isShapeTool ? '' : 'none';
    }
  }

  // --- Brush size ---

  private createBrushPreviewIcon(strokeWidth: number): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.setAttribute('fill', 'none');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 20 Q10 8, 16 16 T28 12');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', String(strokeWidth));
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('fill', 'none');
    svg.appendChild(path);
    return svg;
  }

  private createBrushSizeButton(name: string, size: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'sidebar-size-btn';
    btn.dataset['size'] = String(size);
    btn.title = `${name.charAt(0).toUpperCase() + name.slice(1)} (${size}px)`;
    const strokeWidth = size <= 4 ? 2 : size <= 12 ? 5 : 10;
    btn.replaceChildren(this.createBrushPreviewIcon(strokeWidth));
    btn.addEventListener('click', (): void => this.setCurrentSize(size));
    return btn;
  }

  private applyBrushSize(size: number, notify: boolean): void {
    this.currentSize = Math.max(MIN_SIZE, Math.min(MAX_SIZE, size));
    const shapeTools: ToolType[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];
    if (this.currentTool === 'pen') this.penSize = this.currentSize;
    else if (this.currentTool === 'eraser') this.eraserSize = this.currentSize;
    else if (shapeTools.includes(this.currentTool as ToolType)) this.shapeSize = this.currentSize;
    this.updateBrushSizeButtons();
    if (notify) this.callbacks.onSizeChange(this.currentSize);
  }

  private setCurrentSize(size: number): void {
    this.applyBrushSize(size, true);
  }

  private updateBrushSizeButtons(): void {
    const buttons = this.sidebarEl?.querySelectorAll('.sidebar-size-btn');
    buttons?.forEach((btn): void => {
      const button = btn as HTMLButtonElement;
      const value = parseInt(button.dataset['size'] ?? '0', 10);
      button.classList.toggle('active', value === this.currentSize);
    });
    if (this.sizeSlider) {
      this.animateSliderTo(this.currentSize);
      this.sizeSlider.title = `Size: ${this.currentSize}px`;
    }
    if (this.sizeDisplay) {
      this.sizeDisplay.textContent = `${this.currentSize}`;
    }
  }

  private animateSliderTo(targetValue: number): void {
    if (!this.sizeSlider) return;
    if (this.sliderAnimationId !== null) cancelAnimationFrame(this.sliderAnimationId);
    const startValue = parseInt(this.sizeSlider.value, 10);
    if (startValue === targetValue) { this.sliderAnimationId = null; return; }
    const startTime = Date.now();
    const duration = ANIMATION.toolbarTransition;
    const animate = (): void => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(startValue + (targetValue - startValue) * eased);
      if (this.sizeSlider) this.sizeSlider.value = String(currentValue);
      if (this.sizeDisplay) this.sizeDisplay.textContent = `${currentValue}`;
      if (progress < 1) this.sliderAnimationId = requestAnimationFrame(animate);
      else this.sliderAnimationId = null;
    };
    this.sliderAnimationId = requestAnimationFrame(animate);
  }

  // --- Color swatches ---

  private createColorSwatch(index: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'color-swatch';
    btn.dataset['colorIndex'] = String(index);
    btn.style.backgroundColor = this.colorSwatches[index]!;
    btn.title = 'Click to use, double-click to customize';
    btn.addEventListener('click', (): void => {
      this.currentColor = this.colorSwatches[index]!;
      this.updateColorSwatches();
      this.callbacks.onColorChange(this.currentColor);
    });
    btn.addEventListener('dblclick', (): void => {
      const input = document.createElement('input');
      input.type = 'color';
      input.value = this.colorSwatches[index]!;
      input.addEventListener('input', (): void => {
        this.colorSwatches[index] = input.value;
        btn.style.backgroundColor = input.value;
        this.currentColor = input.value;
        this.updateColorSwatches();
        this.callbacks.onColorChange(this.currentColor);
      });
      input.click();
    });
    return btn;
  }

  private updateColorSwatches(): void {
    const buttons = this.sidebarEl?.querySelectorAll('.color-swatch');
    buttons?.forEach((btn): void => {
      const button = btn as HTMLButtonElement;
      const index = parseInt(button.dataset['colorIndex'] ?? '0', 10);
      button.classList.toggle('active', this.colorSwatches[index] === this.currentColor);
    });
  }

  // --- Diagram style controls ---

  private isDarkMode(): boolean {
    return document.documentElement.dataset['theme'] !== 'light';
  }

  private createPresetPicker(): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'preset-picker';
    this.presetPicker = wrapper;
    this.updatePresetButtons();
    return wrapper;
  }

  private updatePresetButtons(): void {
    if (!this.presetPicker) return;
    // Clear safely
    while (this.presetPicker.firstChild) {
      this.presetPicker.removeChild(this.presetPicker.firstChild);
    }
    const isDark = this.isDarkMode();
    const presets = isDark ? SHAPE_PRESETS_DARK : SHAPE_PRESETS;
    const presetNames = Object.keys(presets);
    for (const name of presetNames.slice(0, 6)) {
      const preset = presets[name]!;
      const btn = document.createElement('button');
      btn.className = 'preset-btn';
      btn.title = name.charAt(0).toUpperCase() + name.slice(1);
      btn.style.background = preset.fill ?? (isDark ? '#374151' : '#ffffff');
      btn.style.borderColor = preset.stroke ?? (isDark ? '#9ca3af' : '#000000');
      btn.addEventListener('click', (): void => {
        const style: { fill?: string; stroke?: string } = {};
        if (preset.fill) style.fill = preset.fill;
        if (preset.stroke) style.stroke = preset.stroke;
        this.callbacks.onStyleChange(style);
        if (this.fillColorInput && preset.fill) this.fillColorInput.value = preset.fill;
        if (this.strokeColorInput && preset.stroke) this.strokeColorInput.value = preset.stroke;
      });
      this.presetPicker.appendChild(btn);
    }
  }

  private createColorPicker(type: 'fill' | 'stroke', title: string): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'color-input-wrapper';
    const label = document.createElement('span');
    label.className = 'color-label';
    label.textContent = title;
    const input = document.createElement('input');
    input.type = 'color';
    input.className = 'color-picker small';
    const isDark = this.isDarkMode();
    if (type === 'fill') {
      input.value = isDark ? '#374151' : '#ffffff';
      this.fillColorInput = input;
    } else {
      input.value = isDark ? '#9ca3af' : '#1a1a1a';
      this.strokeColorInput = input;
    }
    input.title = title;
    input.addEventListener('input', (): void => {
      this.callbacks.onStyleChange({ [type]: input.value });
    });
    wrapper.appendChild(label);
    wrapper.appendChild(input);
    return wrapper;
  }

  private updateColorPickerDefaults(): void {
    const isDark = this.isDarkMode();
    if (this.fillColorInput) this.fillColorInput.value = isDark ? '#374151' : '#ffffff';
    if (this.strokeColorInput) this.strokeColorInput.value = isDark ? '#9ca3af' : '#1a1a1a';
  }

  // --- Connector / arrow helpers ---

  private updateArrowButtons(): void {
    setToggleButtonState(this.startArrowBtn, this.hasStartArrow);
    setToggleButtonState(this.endArrowBtn, this.hasEndArrow);
  }

  private updateLineStyleButtons(): void {
    setToggleButtonState(this.solidBtn, this.currentLineStyle === 'solid');
    setToggleButtonState(this.dashedBtn, this.currentLineStyle === 'dashed');
    setToggleButtonState(this.dottedBtn, this.currentLineStyle === 'dotted');
  }

  private updateArcButton(): void {
    this.arcBtn?.classList.toggle('active', this.hasArc);
  }

  // --- Keyboard shortcuts ---

  private setupKeyboardShortcuts(): void {
    const handleSelectShortcut = (): void => this.switchToTool('select');
    const handlePenShortcut = (): void => this.switchToTool('pen');
    const handleEraserShortcut = (): void => this.switchToTool('eraser');
    const handleTextShortcut = (): void => this.switchToTool('text');
    const handleRectangleShortcut = (): void => this.switchToTool('rectangle');
    const handleEllipseShortcut = (): void => this.switchToTool('ellipse');
    const handleDiamondShortcut = (): void => this.switchToTool('diamond');
    const handleTriangleShortcut = (): void => this.switchToTool('triangle');
    const handleConnectorShortcut = (): void => this.switchToTool('connector');
    const handleToggleGrid = (): void => {
      const next = !this.gridEnabled;
      if (!next) this.gridAutoEnabledBySnap = false;
      this.setGridEnabled(next);
      this.callbacks.onGridToggle?.(next);
    };
    const handleToggleSnap = (): void => {
      const next = !this.snapEnabled;
      if (next && !this.gridEnabled) {
        this.gridAutoEnabledBySnap = true;
        this.setGridEnabled(true);
        this.callbacks.onGridToggle?.(true);
      }
      if (!next && this.gridAutoEnabledBySnap) {
        this.gridAutoEnabledBySnap = false;
        this.setGridEnabled(false);
        this.callbacks.onGridToggle?.(false);
      }
      this.setSnapEnabled(next);
      this.callbacks.onSnapToggle?.(next);
    };
    const handleToggleInvert = (): void => {
      const next = !this.invertEnabled;
      this.setInvertEnabled(next);
      this.callbacks.onInvertToggle?.(next);
    };
    const handleShowShortcuts = (): void => this.callbacks.onShowShortcuts?.();

    this.setShortcutHandler([
      { shortcut: DRAW_SHORTCUTS.toolSelect, action: handleSelectShortcut },
      { shortcut: DRAW_SHORTCUTS.toolPen, action: handlePenShortcut },
      { shortcut: DRAW_SHORTCUTS.toolEraser, action: handleEraserShortcut },
      { shortcut: DRAW_SHORTCUTS.toolText, action: handleTextShortcut },
      { shortcut: DRAW_SHORTCUTS.toolRectangle, action: handleRectangleShortcut },
      { shortcut: DRAW_SHORTCUTS.toolEllipse, action: handleEllipseShortcut },
      { shortcut: DRAW_SHORTCUTS.toolDiamond, action: handleDiamondShortcut },
      { shortcut: DRAW_SHORTCUTS.toolTriangle, action: handleTriangleShortcut },
      { shortcut: DRAW_SHORTCUTS.toolConnector, action: handleConnectorShortcut },
      { shortcut: DRAW_SHORTCUTS.toggleGrid, action: handleToggleGrid },
      { shortcut: DRAW_SHORTCUTS.toggleSnap, action: handleToggleSnap },
      { shortcut: DRAW_SHORTCUTS.toggleInvert, action: handleToggleInvert },
      { shortcut: DRAW_SHORTCUTS.showShortcuts, action: handleShowShortcuts },
    ]);
  }

  // --- Public API ---

  setActiveTool(tool: UnifiedToolbarTool): void {
    this.currentTool = tool;
    this.panMode = false;
    this.updateToolButtons();
    this.updateSliderMode();
    this.updateShapeToolsVisibility();
  }

  setTool(tool: UnifiedToolbarTool): void {
    if (tool !== this.currentTool) {
      this.currentTool = tool;
      this.panMode = false;
      this.updateToolButtons();
      this.updateSliderMode();
      this.updateShapeToolsVisibility();
    }
  }

  setColor(color: string): void {
    this.currentColor = color;
    this.updateColorSwatches();
    this.callbacks.onColorChange(color);
    const customPicker = this.sidebarEl?.querySelector('.custom-color-picker') as HTMLInputElement | null;
    if (customPicker) customPicker.value = color;
  }

  setInvertMode(inverted: boolean): void {
    this.setInvertEnabled(inverted);
  }

  setBrushSize(size: number): void {
    this.applyBrushSize(size, false);
  }

  updateDeleteButton(hasSelection: boolean): void {
    if (this.deleteSelectedBtn) this.deleteSelectedBtn.disabled = !hasSelection;
  }

  private textFormatVisible = false;

  updateTextFormatVisibility(visible: boolean, textStyle: TextStyle | null): void {
    if (this.textFormatRow && visible !== this.textFormatVisible) {
      this.textFormatVisible = visible;
      if (visible) {
        const rect = this.container.getBoundingClientRect();
        this.textFormatRow.style.setProperty('--toolbar-bottom', `${rect.bottom}px`);
      }
      this.textFormatRow.style.display = visible ? '' : 'none';
    }
    if (textStyle) {
      this.currentTextStyle = { ...this.currentTextStyle, ...textStyle };
      this.updateTextFormatButtons();
    }
  }

  showConnectorOptions(startArrow: ArrowHead, endArrow: ArrowHead, lineStyle: LineStyle = 'solid', hasArc = false): void {
    this.hasStartArrow = startArrow !== 'none';
    this.hasEndArrow = endArrow !== 'none';
    this.currentLineStyle = lineStyle;
    this.hasArc = hasArc;
    this.updateArrowButtons();
    this.updateLineStyleButtons();
    this.updateArcButton();
    if (this.connectorOptionsSection) this.connectorOptionsSection.classList.add('visible');
  }

  hideConnectorOptions(): void {
    if (this.connectorOptionsSection) this.connectorOptionsSection.classList.remove('visible');
  }

  setArcEnabled(enabled: boolean): void {
    this.hasArc = enabled;
    this.updateArcButton();
  }

  updateStrokeWidth(width: number): void {
    this.currentStrokeWidth = width;
    this.syncStrokeWidthUI();
  }

  private syncStrokeWidthUI(): void {
    if (this.strokeWidthSlider) {
      this.strokeWidthSlider.value = String(this.currentStrokeWidth);
      this.strokeWidthSlider.title = `Width: ${this.currentStrokeWidth}`;
    }
    if (this.strokeWidthDisplay) {
      this.strokeWidthDisplay.textContent = String(this.currentStrokeWidth);
    }
  }

  protected onPanModeChange(): void {
    this.updateToolButtons();
  }

  destroy(): void {
    this.themeObserver?.disconnect();
    this.clearKeydownHandler();
    this.sidebarEl?.remove();
    this.connectorOptionsSection?.remove();
    this.textFormatRow?.remove();
  }
}
